import * as React from 'react';

import { cn, SearchField } from '@rematter/ui-web';

// =============================================================================
// SelectionListItem - A reusable list item for the left panel
// =============================================================================

export type SelectionListItemProps = {
  name: string;
  subtitle?: React.ReactNode;
  tagIcon?: React.ReactNode;
  tagCount?: number | string;
  isSelected?: boolean;
  onClick?: () => void;
  className?: string;
  nameClassName?: string;
};

export const SelectionListItem: React.FC<SelectionListItemProps> = ({
  name,
  subtitle,
  tagIcon,
  tagCount,
  isSelected,
  onClick,
  className,
  nameClassName,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      '@SelectionListItem flex w-full flex-col gap-0.5 rounded-lg border-2 border-transparent px-2 py-1.5 text-left transition-colors',
      'hover:bg-neutral/5',
      isSelected && 'border-primary bg-[#fbfbfc]',
      className,
    )}
  >
    <div className="flex items-center justify-between">
      <span
        className={cn(
          'truncate font-semibold',
          isSelected ? 'text-neutral' : 'text-neutral/80',
          nameClassName,
        )}
      >
        {name}
      </span>
    </div>
    {(tagIcon || tagCount !== undefined || subtitle) && (
      <div className="flex items-center gap-1 text-sm text-neutral/60">
        {tagIcon}
        {tagCount !== undefined && <span>{tagCount}</span>}
        {subtitle}
      </div>
    )}
  </button>
);

// =============================================================================
// TwoPanelSelectionLayout - Main layout component
// =============================================================================

export type TwoPanelSelectionLayoutProps<T> = {
  // Left panel - list of items
  items: T[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  getItemId: (item: T) => string;
  renderItem: (item: T, isSelected: boolean) => React.ReactNode;

  // Search
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;

  // Right panel - detail view
  renderDetail: (selectedItem: T | null) => React.ReactNode;

  // Optional
  emptyState?: React.ReactNode;
  className?: string;
  leftPanelWidth?: number;
};

export function TwoPanelSelectionLayout<T>({
  items,
  selectedId,
  onSelect,
  getItemId,
  renderItem,
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  renderDetail,
  emptyState,
  className,
  leftPanelWidth = 240,
}: TwoPanelSelectionLayoutProps<T>) {
  const listRef = React.useRef<HTMLDivElement>(null);

  // Find the selected item
  const selectedItem = React.useMemo(
    () => items.find((item) => getItemId(item) === selectedId) ?? null,
    [items, selectedId, getItemId],
  );

  // Keyboard navigation
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (items.length === 0) return;

      const currentIndex = selectedId
        ? items.findIndex((item) => getItemId(item) === selectedId)
        : -1;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextIndex =
          currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        onSelect(getItemId(items[nextIndex]));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prevIndex =
          currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        onSelect(getItemId(items[prevIndex]));
      }
    },
    [items, selectedId, onSelect, getItemId],
  );

  return (
    <div
      className={cn('@TwoPanelSelectionLayout flex h-full', className)}
      onKeyDown={handleKeyDown}
    >
      {/* Left Panel - Selection List */}
      <div
        className="flex shrink-0 flex-col border-r border-neutral/20"
        style={{ width: leftPanelWidth }}
      >
        {/* Search Field */}
        {onSearchChange && (
          <div className="p-3 pb-0">
            <SearchField
              value={searchValue}
              onChange={onSearchChange}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className="h-8 w-full"
            />
          </div>
        )}

        {/* Scrollable List */}
        <div
          ref={listRef}
          className="flex-1 overflow-y-auto pt-3"
          role="listbox"
          aria-label="Selection list"
        >
          {items.length === 0
            ? (emptyState ?? (
                <div className="p-4 text-center text-sm text-neutral/60">
                  No items found
                </div>
              ))
            : items.map((item, index) => {
                const id = getItemId(item);
                const isSelected = id === selectedId;

                const prevIsSelected =
                  index > 0 && getItemId(items[index - 1]) === selectedId;

                return (
                  <div
                    key={id}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => onSelect(id)}
                    className="cursor-pointer"
                  >
                    {index > 0 && !isSelected && !prevIsSelected && (
                      <div className="mx-2 border-t border-neutral/10" />
                    )}
                    {renderItem(item, isSelected)}
                  </div>
                );
              })}
        </div>
      </div>

      {/* Right Panel - Detail View */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {renderDetail(selectedItem)}
      </div>
    </div>
  );
}
